import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material'
import {
  useCreateAdminCmsContentMutation,
  useDeleteAdminCmsContentMutation,
  useGetAdminCmsContentQuery,
  useUpdateAdminCmsContentMutation,
} from '@store/api/adminApi'
import AdminPageGate from '@components/auth/AdminPageGate'

const STATUSES = ['draft', 'published', 'archived'] as const
const TYPES = ['article', 'faq', 'page'] as const
const CATEGORIES = ['general', 'help', 'marketing', 'legal', 'announcement'] as const

const statusColor = (status: string): 'default' | 'success' | 'warning' => {
  if (status === 'published') return 'success'
  if (status === 'archived') return 'warning'
  return 'default'
}

const ContentManagement: React.FC = () => {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [search, setSearch] = useState('')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const [slug, setSlug] = useState('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [excerpt, setExcerpt] = useState('')
  const [category, setCategory] = useState('help')
  const [contentType, setContentType] = useState('article')
  const [status, setStatus] = useState('draft')

  const [editOpen, setEditOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [editSlug, setEditSlug] = useState('')
  const [editTitle, setEditTitle] = useState('')
  const [editBody, setEditBody] = useState('')
  const [editExcerpt, setEditExcerpt] = useState('')
  const [editCategory, setEditCategory] = useState('help')
  const [editType, setEditType] = useState('article')
  const [editStatus, setEditStatus] = useState('draft')

  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleteLabel, setDeleteLabel] = useState('')

  const { data, isLoading, refetch } = useGetAdminCmsContentQuery({
    status: statusFilter || undefined,
    category: categoryFilter || undefined,
    search: search.trim() || undefined,
    page,
    limit: 20,
  })
  const [createContent, { isLoading: isCreating }] = useCreateAdminCmsContentMutation()
  const [updateContent, { isLoading: isUpdating }] = useUpdateAdminCmsContentMutation()
  const [deleteContent, { isLoading: isDeleting }] = useDeleteAdminCmsContentMutation()

  const items = data?.items ?? []
  const pagination = data?.pagination
  const totalItems = pagination?.total ?? items.length

  const showError = (err: unknown, fallback: string) => {
    const apiErr = err as { data?: { message?: string } }
    setMessage({ type: 'error', text: apiErr?.data?.message ?? fallback })
  }

  const resetCreateForm = () => {
    setSlug('')
    setTitle('')
    setBody('')
    setExcerpt('')
    setCategory('help')
    setContentType('article')
    setStatus('draft')
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    try {
      await createContent({
        slug: slug.trim() || undefined,
        title: title.trim(),
        body: body.trim(),
        excerpt: excerpt.trim() || undefined,
        category,
        contentType,
        status,
      }).unwrap()
      resetCreateForm()
      setMessage({ type: 'success', text: 'Content created' })
      refetch()
    } catch (err: unknown) {
      showError(err, 'Failed to create content')
    }
  }

  const openEdit = (item: (typeof items)[number]) => {
    setEditId(item.id)
    setEditSlug(item.slug)
    setEditTitle(item.title)
    setEditBody(item.body)
    setEditExcerpt(item.excerpt ?? '')
    setEditCategory(item.category)
    setEditType(item.contentType)
    setEditStatus(item.status)
    setEditOpen(true)
  }

  const handleUpdate = async () => {
    if (!editId) return
    setMessage(null)
    try {
      await updateContent({
        id: editId,
        updates: {
          slug: editSlug.trim(),
          title: editTitle.trim(),
          body: editBody.trim(),
          excerpt: editExcerpt.trim() || null,
          category: editCategory,
          contentType: editType,
          status: editStatus,
        },
      }).unwrap()
      setEditOpen(false)
      setMessage({ type: 'success', text: 'Content updated' })
      refetch()
    } catch (err: unknown) {
      showError(err, 'Failed to update content')
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    setMessage(null)
    try {
      await deleteContent(deleteId).unwrap()
      setDeleteId(null)
      setMessage({ type: 'success', text: 'Content deleted' })
      refetch()
    } catch (err: unknown) {
      showError(err, 'Failed to delete content')
    }
  }

  return (
    <AdminPageGate permission="content:write" title="Content CMS">
    <Box>
      <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
        Content CMS
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Manage help articles, FAQs, and marketing pages. For Help Center visibility use category <strong>help</strong>, status{' '}
        <strong>published</strong>, and tags such as <code>section:Getting Started</code>, <code>video</code>,{' '}
        <code>duration:3:45</code>, <code>tip:warning</code>, or <code>contextual-tip</code> (page type).
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Create Content
          </Typography>
          <Box component="form" onSubmit={handleCreate}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required fullWidth />
                <TextField
                  label="Slug (optional)"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="auto-from-title"
                  fullWidth
                />
              </Stack>
              <TextField
                label="Body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
                fullWidth
                multiline
                minRows={4}
              />
              <TextField
                label="Excerpt (optional)"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="create-category-label">Category</InputLabel>
                  <Select
                    labelId="create-category-label"
                    label="Category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {CATEGORIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel id="create-type-label">Type</InputLabel>
                  <Select
                    labelId="create-type-label"
                    label="Type"
                    value={contentType}
                    onChange={(e) => setContentType(e.target.value)}
                  >
                    {TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel id="create-status-label">Status</InputLabel>
                  <Select
                    labelId="create-status-label"
                    label="Status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    {STATUSES.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button type="submit" variant="contained" disabled={isCreating || !title.trim() || !body.trim()}>
                  {isCreating ? 'Creating…' : 'Create'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Search title or slug"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              size="small"
              fullWidth
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="filter-status-label">Status</InputLabel>
              <Select
                labelId="filter-status-label"
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setPage(1)
                }}
              >
                <MenuItem value="">All</MenuItem>
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="filter-category-label">Category</InputLabel>
              <Select
                labelId="filter-category-label"
                label="Category"
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value)
                  setPage(1)
                }}
              >
                <MenuItem value="">All</MenuItem>
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Typography variant="h6" gutterBottom>
            Content ({totalItems})
          </Typography>

          {isLoading ? (
            <CircularProgress size={28} />
          ) : items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No content matches this filter.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Slug</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.title}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {item.slug}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.contentType}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell>
                        <Chip label={item.status} size="small" color={statusColor(item.status)} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(item)} aria-label={`Edit ${item.title}`}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setDeleteId(item.id)
                              setDeleteLabel(item.title)
                            }}
                            aria-label={`Delete ${item.title}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pagination && pagination.totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={pagination.totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Content</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} fullWidth />
            <TextField label="Slug" value={editSlug} onChange={(e) => setEditSlug(e.target.value)} fullWidth />
            <TextField
              label="Body"
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              fullWidth
              multiline
              minRows={6}
            />
            <TextField
              label="Excerpt"
              value={editExcerpt}
              onChange={(e) => setEditExcerpt(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="edit-category-label">Category</InputLabel>
                <Select
                  labelId="edit-category-label"
                  label="Category"
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                >
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="edit-type-label">Type</InputLabel>
                <Select
                  labelId="edit-type-label"
                  label="Type"
                  value={editType}
                  onChange={(e) => setEditType(e.target.value)}
                >
                  {TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="edit-status-label">Status</InputLabel>
                <Select
                  labelId="edit-status-label"
                  label="Status"
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={isUpdating}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete content?</DialogTitle>
        <DialogContent>
          <Typography>Delete &quot;{deleteLabel}&quot;? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={isDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </AdminPageGate>
  )
}

export default ContentManagement
